import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectIsLoggedIn,
  selectIsAuthLoading,
  selectIsAuthChecked,
  logout,
} from '@/features/auth/redux';
import { icons } from '@/images';

interface LoginButtonProps {
  readonly className?: string;
}

/**
 * Logout-only button. Login is handled by the MMT global header.
 * Only renders when the user is logged in.
 */
export function LoginButton({ className = '' }: LoginButtonProps) {
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const isLoading = useAppSelector(selectIsAuthLoading);
  const isAuthChecked = useAppSelector(selectIsAuthChecked);

  // Hide until auth state is known, and hide when not logged in
  if (!isAuthChecked || !isLoggedIn) {
    return null;
  }

  const handleLogout = () => {
    void dispatch(logout());
  };

  return (
    <button
      onClick={handleLogout}
      disabled={isLoading}
      className={`bg-gradient-button flex cursor-pointer items-center rounded-[58px] border-0 bg-transparent p-[3px] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
      type="button"
      aria-label="Logout"
    >
      <div className="flex items-center gap-2 rounded-[53px] bg-white px-4 py-2">
        <img src={icons.MyLoginIcon} alt="profile" className="h-[26px] w-[26px] object-contain" />
        {isLoading && (
          <div
            className="h-4 w-4 animate-spin rounded-full border-2 border-transparent border-t-white"
            role="status"
            aria-label="Logging out"
          />
        )}
        <span className="text-high-emphasis text-sm font-bold whitespace-nowrap">
          {isLoading ? 'Logging out...' : 'Log out'}
        </span>
      </div>
    </button>
  );
}
